import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

import { authService } from '@/core/auth/export';
import notificationApi from '@/core/notifications/api/notification-api';
import {
  getPushSubscriptionStatus,
  enablePushSubscription,
} from '@/core/notifications/services/push-subscription-service';
import { LocalStorageUtil } from '@/shared/storage/export';

const localStorage = new LocalStorageUtil();
const TOAST_ID = 'push-enable-prompt';

function dismissKey(subjectId: string) {
  return `push-prompt-dismissed:${subjectId}`;
}

function isDismissed(subjectId: string): boolean {
  return !!localStorage.getValue<boolean>(dismissKey(subjectId));
}

function setDismissed(subjectId: string, value: boolean) {
  if (value) localStorage.saveValue(dismissKey(subjectId), true);
  else localStorage.removeValue(dismissKey(subjectId));
}

function PushPromptToastBody({ subjectId }: { subjectId: string }) {
  const handleEnable = () => {
    toast.dismiss(TOAST_ID);
    enablePushSubscription()
      .catch(() => 'error' as const)
      .then(status => {
        if (status !== 'on') toast.error('Could not enable push notifications on this device.');
      });
  };

  const handleDontAskAgain = () => {
    setDismissed(subjectId, true);
    toast.dismiss(TOAST_ID);
  };

  return (
    <div>
      <div style={{ fontSize: 14 }}>Do you want notifications on this device?</div>
      <div className="d-flex align-items-center gap-2 mt-2">
        <button className="btn btn-sm btn-tertiary text-base-light" onClick={handleEnable}>
          Sure, I&apos;m in
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={handleDontAskAgain}>
          No, don&apos;t ask again
        </button>
      </div>
    </div>
  );
}

export async function maybePromptPushEnable(): Promise<void> {
  const subject = authService.getSubjectData();
  if (!subject?.id || isDismissed(subject.id)) return;

  let contacts, subscriptions;
  try {
    [contacts, subscriptions] = await Promise.all([
      notificationApi.getContacts(),
      notificationApi.getSubscriptions(),
    ]);
  } catch {
    return;
  }
  const status = await getPushSubscriptionStatus(contacts).catch(() => 'error' as const);
  if (status !== 'off') return;

  const hasPushSubscription = subscriptions.some(s => s.defaultChannels.includes('push'));
  if (!hasPushSubscription) return;

  toast.info(<PushPromptToastBody subjectId={subject.id} />, {
    toastId: TOAST_ID,
    autoClose: false,
    closeOnClick: false,
    icon: <FontAwesomeIcon icon={faBell} />,
  });
}

export default function PushEnablePrompt() {
  useEffect(() => {
    maybePromptPushEnable();
  }, []);

  return null;
}
